import { useEffect, useMemo } from 'react'
import { useAppStore } from '../../../store/app'
import type { TranscriptEntry } from '../../../store/types'
import { Markdown } from '../../permission/Markdown'
import { DiffView } from '../../permission/DiffView'
import { toolIcon } from '../../permission/toolIcon'
import { MessageBubble } from '../../../components/MessageBubble'
import { useStickToBottom } from '../../../components/useStickToBottom'
import { sendRequestTranscriptHistory, sendWakeAgent } from '../../../api/ws'
import { InputBar } from './InputBar'
import { VoiceSessionBar } from './VoiceSessionBar'
import './TranscriptSegment.css'

// Stable empty bucket so the per-agent selector returns the same reference
// across renders when an agent has no transcript entries yet.
const EMPTY: TranscriptEntry[] = []

export function TranscriptSegment({
  agentInstanceID,
  isActive,
  pidStatus,
}: {
  agentInstanceID: string
  isActive: boolean
  pidStatus: string
}) {
  const humanUserID = useAppStore((s) => s.humanUserID)
  const showSystemMessages = useAppStore((s) => s.showSystemMessages)
  const agentName = useAppStore(
    (s) => s.agentInstances.find((a) => a.id === agentInstanceID)?.name,
  )
  // This agent's own bucket — already filtered and pruned in the store, so no
  // cross-agent filtering here. EMPTY is a module const so the selector returns
  // a stable reference when the agent has no entries yet (avoids render churn).
  const transcript = useAppStore((s) => s.transcripts[agentInstanceID] ?? EMPTY)

  // Pin to the bottom only while the reader is already there; once they scroll
  // up to read, new entries stop yanking their position and raise a badge
  // instead. Feed switch keyed on agentInstanceID re-pins.
  const { scrollRef, onScroll, hasNew, scrollToBottom } = useStickToBottom(
    transcript.length,
    agentInstanceID,
  )

  // Ask the daemon to replay this agent's on-disk JSONL transcript on every
  // segment open. The store dedups by content-derived id so re-fetching when
  // the user navigates away and back is harmless; we accept the redundant
  // round trip for the simpler "always fresh" guarantee.
  useEffect(() => {
    if (!agentInstanceID) return
    sendRequestTranscriptHistory(agentInstanceID)
  }, [agentInstanceID])

  // `status` (scheduler intent) and `pid_status` (actual process liveness) are
  // orthogonal: an agent whose process crashed or exited on its own keeps
  // isActive=true (intent unchanged) but a dead pid_status. Distinguish those
  // "stopped" states from a genuine spawn-in-progress — otherwise the empty
  // state below shows a "Starting agent…" spinner for a process that is NOT
  // starting, which reads as "respawning" and then hangs forever (nothing here
  // ever wakes it). Re-spawn stays user-initiated (the button); we never wake
  // automatically.
  const isDeadPid =
    pidStatus === 'exited' || pidStatus === 'killed' || pidStatus === 'spawn_failed'
  const isStartingPid = pidStatus === 'never_spawned' || pidStatus === 'spawning'
  // Intent is still "run it", but the process is gone → offer a manual respawn.
  const isStopped = isActive && isDeadPid

  // Drop each 'queued_user' whose message later arrived for real. A swallowed
  // message and its redelivery are the SAME message, and the host-side inbox
  // makes the redelivery the normal outcome — so without this the transcript
  // would show every mid-turn message twice, once pending and once landed.
  // Matching is on the envelope's mid, which is carried verbatim through both.
  const visibleTranscript = useMemo(() => {
    const landed = new Set<string>()
    // Mids the host has confirmed as swallowed. When one of these exists, the
    // sender's own optimistic bubble for the same message is redundant: both
    // rows say the same thing, and the host's carries the real envelope,
    // sender attribution and timestamp.
    const hostQueued = new Set<string>()
    for (const e of transcript) {
      if (e.type === 'user') {
        for (const seg of parseHearthSegments(e.text ?? '')) {
          if (seg.envelope?.mid) landed.add(seg.envelope.mid)
        }
      } else if (e.type === 'queued_user' && !e.id.startsWith('local-')) {
        const mid = queuedMID(e)
        if (mid) hostQueued.add(mid)
      }
    }
    if (landed.size === 0 && hostQueued.size === 0) return transcript
    return transcript.filter((e) => {
      if (e.type !== 'queued_user') return true
      const mid = queuedMID(e)
      if (!mid) return true
      // Landed for real → neither copy belongs in the transcript any more.
      if (landed.has(mid)) return false
      // Otherwise keep exactly one: the host's over the local placeholder.
      return !(e.id.startsWith('local-') && hostQueued.has(mid))
    })
  }, [transcript])

  return (
    <div className="transcript">
      <div className="transcript__body">
      <div className="transcript__scroll" ref={scrollRef} onScroll={onScroll}>
        {transcript.length === 0 ? (
          <div className="transcript__empty">
            {pidStatus === 'host_disconnected' ? (
              <>
                <div className="transcript__empty-icon" aria-hidden>📡</div>
                <div className="transcript__empty-text">Host disconnected.</div>
              </>
            ) : isStopped ? (
              <>
                <div className="transcript__empty-icon" aria-hidden>🛑</div>
                <div className="transcript__empty-text">
                  This agent has stopped. Re-spawn it to pick the conversation
                  back up.
                </div>
              </>
            ) : isStartingPid && isActive ? (
              <>
                <div className="transcript__starting-spinner" aria-hidden />
                <div className="transcript__empty-text">Starting agent…</div>
              </>
            ) : (
              <>
                <div className="transcript__empty-icon" aria-hidden>
                  {isActive ? '💬' : '📄'}
                </div>
                <div className="transcript__empty-text">
                  {isActive
                    ? 'Send a message to start the conversation.'
                    : 'No transcript entries.'}
                </div>
              </>
            )}
          </div>
        ) : (
          visibleTranscript.map((entry, i) => (
            <TranscriptRow
              key={entry.id}
              entry={entry}
              prevEntry={i > 0 ? visibleTranscript[i - 1] : null}
              agentName={agentName ?? 'Claude'}
              humanUserID={humanUserID ?? ''}
              showSystemMessages={showSystemMessages}
            />
          ))
        )}
      </div>
        {hasNew && (
          <button
            type="button"
            className="transcript__jump"
            onClick={() => scrollToBottom()}
          >
            <span className="transcript__jump-arrow" aria-hidden>↓</span>
            New messages
          </button>
        )}
      </div>
      {isActive && pidStatus === 'running' ? (
        <>
          {/* Above the composer rather than in it: a live voice session is a
              state the room can see, not a button among buttons. Only rendered
              alongside a usable input bar — offering voice mode for an agent
              that cannot receive a message would be a dead control. */}
          <VoiceSessionBar agentInstanceID={agentInstanceID} />
          <InputBar agentInstanceID={agentInstanceID} />
        </>
      ) : isStopped ? (
        // A stopped agent (crashed/exited but still "active" intent) gets an
        // explicit respawn affordance instead of a hidden/absent input bar or a
        // misleading spinner. Tapping it wakes the agent — user-initiated only;
        // opening this screen never respawns on its own. Mirrors the "Re-spawn"
        // action in AgentRow's long-press menu (same wake_ai_agent_instance).
        <div className="transcript__respawn-bar">
          <span className="transcript__respawn-text">Agent stopped</span>
          <button
            type="button"
            className="transcript__respawn-btn"
            onClick={() => sendWakeAgent(agentInstanceID)}
          >
            ✨ Re-spawn
          </button>
        </div>
      ) : null}
    </div>
  )
}

// Agent-directed system_event `event`s that are operational FYIs for the agent
// (re-read plugin docs, a connection changed) but noise for a human reading the
// transcript. Hidden unless the debug toggle is on. permission_dispatch /
// permission_resolved are human-facing audit and stay visible either way.
const AGENT_FYI_EVENTS = new Set(['resource_granted', 'resource_revoked', 'resource_updated'])

// The exact marker an agent is instructed to emit (as its entire message) when
// it insists on acknowledging a system notification it was told to ignore. See
// the system_event block in cli/agent.go. Hidden unless the debug toggle is on.
const SILENT_MARKER = '<hearth-silent/>'

// Returns the "sender side" of an entry for gap-top logic.
// user → 'user'; text/tool_use/tool_result → 'agent'; system → 'system'.
// A coalesced user entry holds multiple segments; the LAST non-hidden one is
// what visually precedes the next entry, so we report that.
function senderSide(entry: TranscriptEntry): 'user' | 'agent' | 'system' | 'hidden' {
  // A queued message is the user's, however it ended up — it belongs on their
  // side of the conversation for gap-spacing purposes.
  if (entry.type === 'queued_user') return 'user'
  if (entry.type !== 'user') return 'agent'
  const segs = parseHearthSegments(entry.text ?? '')
  for (let i = segs.length - 1; i >= 0; i--) {
    const s = segmentSide(segs[i])
    if (s !== 'hidden') return s
  }
  return 'hidden'
}

// Side of a single parsed segment. Ignores the show-system toggle: a
// system_event hidden by the toggle still counts as 'system' here, matching the
// prior behavior in which system rows never introduce a gap.
function segmentSide(seg: ParsedEnvelope): 'user' | 'system' | 'hidden' {
  const k = seg.envelope?.kind
  if (k === 'system_event') return 'system'
  if (k === 'scheduled_trigger') return 'system'
  if (k === 'chat_context') return 'hidden'
  return 'user'
}

// A queued_user row's message id. An optimistic bubble carries it on the entry
// (from relay_input_ack); a host-sent one carries it inside its envelope text.
function queuedMID(e: TranscriptEntry): string | undefined {
  return e.mid ?? parseHearthSegments(e.text ?? '')[0]?.envelope?.mid
}

// The one line under a message that isn't a turn yet. Four states, because they
// mean genuinely different things to the person who sent it: still handing it to
// the server, refused outright, accepted and waiting on a busy agent, or
// accepted and then given up on.
//
// The last one is the reason this component exists at all. Before the host
// reported abandoned messages, an undelivered message sat on "waiting" forever —
// a row that quietly lied for as long as you left the app open.
function QueuedNote({
  sendState,
  reason,
}: {
  sendState?: 'sending' | 'failed' | 'undelivered'
  reason?: string
}) {
  if (sendState === 'undelivered') {
    return (
      <div className="trow__queued-note trow__queued-note--failed">
        <span aria-hidden>⚠️</span> Never reached the agent
        {reason ? <span className="trow__queued-reason"> — {reason}</span> : null}
      </div>
    )
  }
  if (sendState === 'failed') {
    return (
      <div className="trow__queued-note trow__queued-note--failed">
        <span aria-hidden>⚠️</span> Not sent
      </div>
    )
  }
  if (sendState === 'sending') {
    return (
      <div className="trow__queued-note">
        <span aria-hidden>⏳</span> Sending…
      </div>
    )
  }
  return (
    <div className="trow__queued-note">
      <span aria-hidden>⏳</span> Waiting for the agent to finish
    </div>
  )
}

function TranscriptRow({
  entry,
  prevEntry,
  agentName,
  humanUserID,
  showSystemMessages,
}: {
  entry: TranscriptEntry
  prevEntry: TranscriptEntry | null
  agentName: string
  humanUserID: string
  showSystemMessages: boolean
}) {
  const rel = relTime(entry.timestamp)
  const side = senderSide(entry)
  const prevSide = prevEntry ? senderSide(prevEntry) : null
  // Add extra top spacing when the sender switches between user and agent.
  // 'hidden' entries don't render, so treat them as transparent for gap logic.
  const gapTop =
    prevSide !== null &&
    prevSide !== 'hidden' &&
    prevSide !== side &&
    side !== 'system' &&
    side !== 'hidden'

  if (entry.type === 'user') {
    // One entry may carry several coalesced segments (see parseHearthSegments).
    // Render each as its own row so a human message never gets hidden behind a
    // leading system_event. Gap spacing carries across segment boundaries: seed
    // from the previous entry's trailing side, then advance per rendered segment.
    const segments = parseHearthSegments(entry.text ?? '')
    let lastSide: 'user' | 'agent' | 'system' | 'hidden' | null = prevSide
    return (
      <>
        {segments.map((seg, idx) => {
          const s = segmentSide(seg)
          const gap =
            lastSide !== null &&
            lastSide !== 'hidden' &&
            lastSide !== s &&
            s !== 'system' &&
            s !== 'hidden'
          if (s !== 'hidden') lastSide = s
          return (
            <UserSegmentRow
              key={idx}
              seg={seg}
              humanUserID={humanUserID}
              showSystemMessages={showSystemMessages}
              entryTimestamp={entry.timestamp}
              gapTop={gap}
            />
          )
        })}
      </>
    )
  }

  // Sent but not received: the harness was mid-turn and absorbed it. Rendered
  // as the user's own message, dimmed, with a "waiting" note — because from the
  // sender's side that IS what happened, and showing nothing at all is the bug
  // this fixes. The row disappears when the inbox's redelivery lands (the list
  // filters it out once the same mid shows up as a real turn).
  if (entry.type === 'queued_user') {
    const seg = parseHearthSegments(entry.text ?? '')[0]
    const envelope = seg?.envelope
    const sender = envelope?.human ?? envelope?.from
    const isMe = !sender?.id || sender.id === humanUserID
    const parsedTs = envelope?.ts ? Date.parse(envelope.ts) : NaN
    const ts = Number.isNaN(parsedTs) ? entry.timestamp : parsedTs
    return (
      <div className="trow--queued">
        <MessageBubble
          variant={isMe ? 'mine' : 'theirs'}
          senderName={isMe ? undefined : sender?.name || 'Someone'}
          time={relTime(ts)}
          gapTop={gapTop}
        >
          <div className="trow__md">
            <Markdown source={seg?.body ?? entry.text ?? ''} />
          </div>
          <QueuedNote sendState={entry.send_state} reason={entry.undeliverable_reason} />
        </MessageBubble>
      </div>
    )
  }

  if (entry.type === 'text') {
    // The agent's "I insist on acknowledging but there's nothing to say" marker.
    // Normally suppressed as noise; when debugging, shown as a muted system note
    // so it's clearly a non-response, not real agent output.
    if ((entry.text ?? '').trimStart().startsWith(SILENT_MARKER)) {
      if (!showSystemMessages) return null
      return <SystemEventRow body={`${agentName} acknowledged silently (${SILENT_MARKER})`} />
    }
    // Part or all of this reply was spoken on a voice satellite. The relay already
    // stripped the marker, so entry.text is clean; entry.voice says which words
    // were actually voiced. When the whole message was spoken, quoting it back
    // would just print it twice — so the note stays bare in that case.
    const voice = entry.voice
    const quote =
      voice && voice.spoken.trim() !== (entry.text ?? '').trim() ? voice.spoken : undefined
    return (
      <MessageBubble
        variant="agent"
        senderName={agentName}
        time={rel}
        gapTop={gapTop}
      >
        {entry.text && (
          <div className="trow__md">
            <Markdown source={entry.text} />
          </div>
        )}
        {voice ? <SpokenAloudNote listening={voice.listen} quote={quote} /> : null}
      </MessageBubble>
    )
  }

  if (entry.type === 'tool_use') {
    const name = entry.tool_name ?? 'Tool'
    const input = entry.tool_input ?? null
    const icon = toolIcon(name)
    return (
      <div className={`trow trow--tool${gapTop ? ' trow--gap-top' : ''}`}>
        <div className="trow__tool-card">
          <div className="trow__tool-head">
            <span className="trow__tool-icon">{icon}</span>
            <span className="trow__tool-name">{name}</span>
            <span className="trow__tool-time">{rel}</span>
          </div>
          {name === 'Edit' && input ? (
            <div className="trow__edit">
              {input.file_path != null && (
                <code className="trow__filepath">{String(input.file_path)}</code>
              )}
              <DiffView
                before={String(input.old_string ?? '')}
                after={String(input.new_string ?? '')}
              />
            </div>
          ) : input?.file_path != null ? (
            <code className="trow__filepath">{String(input.file_path)}</code>
          ) : entry.text ? (
            <div className="trow__preview">{entry.text.slice(0, 200)}</div>
          ) : null}
        </div>
      </div>
    )
  }

  // tool_result — compact indented snippet; skip if empty
  if (!entry.text) return null
  return (
    <div className="trow trow--result">
      <div className="trow__result-card">
        <pre className="trow__result-preview">{entry.text.slice(0, 150)}</pre>
      </div>
    </div>
  )
}

// One hearth/1 segment of a user entry: a human message bubble, a visible
// system_event note, or nothing (an agent-directed FYI / chat scaffolding).
// Its own component so each segment owns its long-press handler (a single entry
// can hold several segments, and hooks can't be called in a loop).
function UserSegmentRow({
  seg,
  humanUserID,
  showSystemMessages,
  entryTimestamp,
  gapTop,
}: {
  seg: ParsedEnvelope
  humanUserID: string
  showSystemMessages: boolean
  entryTimestamp: number
  gapTop: boolean
}) {
  const envelope = seg.envelope
  const body = seg.body

  if (envelope?.kind === 'system_event') {
    // Agent-directed FYIs (resource/plugin changes) are noise for a human —
    // hide unless debugging. Human-facing audit events stay visible.
    if (!showSystemMessages && AGENT_FYI_EVENTS.has(envelope.event ?? '')) {
      return null
    }
    return <SystemEventRow body={body} />
  }
  // Injected agent scaffolding (chat @mention context) — hide from transcript.
  if (envelope?.kind === 'chat_context') {
    return null
  }
  // Automated Routine kickoff — render as a banner, not a fake human bubble
  // (no human typed this at 5am).
  if (envelope?.kind === 'scheduled_trigger') {
    return <RoutineBannerRow body={body} />
  }
  // Someone spoke this out loud to a satellite. Render what they SAID: the body
  // is the agent's copy (instructions first, words last) and showing it would put
  // a paragraph of machine-directed preamble in front of six spoken words.
  const spokenBody = envelope?.kind === 'voice' ? envelope.voice?.said : undefined

  // Attribute the sender. On hearth/2 `from` is the transmitting device and
  // `human` is the person, so read the human as `human ?? from` (on hearth/1
  // `from` IS the human). A shared-device message has no `human`, so it falls
  // through to the device and renders under its name ("Kitchen Speaker"). Legacy
  // single-user agents send un-enveloped text with no sender, which we treat as
  // the logged-in user (there was only ever one human on those). Anyone else —
  // another human, a shared device, OR another agent — renders as a named
  // 'theirs' row.
  const sender = envelope?.human ?? envelope?.from
  const isMe = !sender?.id || sender.id === humanUserID
  // Coalesced segments can span a minute or two, so prefer each segment's own
  // envelope timestamp over the (single) entry timestamp when present.
  const parsedTs = envelope?.ts ? Date.parse(envelope.ts) : NaN
  const ts = Number.isNaN(parsedTs) ? entryTimestamp : parsedTs
  return (
    <MessageBubble
      variant={isMe ? 'mine' : 'theirs'}
      senderName={isMe ? undefined : sender?.name || 'Someone'}
      time={relTime(ts)}
      gapTop={gapTop}
    >
      {/* Human transcript messages render as Markdown too, mirroring chat —
          the same lingua franca on both surfaces. */}
      <div className="trow__md">
        <Markdown source={spokenBody ?? body} />
      </div>
      {spokenBody !== undefined ? <SpokenAloudNote heard /> : null}
    </MessageBubble>
  )
}

// The one line that marks a message as SPOKEN rather than typed. It appears on
// both sides of a voice exchange: `heard` on what a person said into a satellite,
// and plain on what the agent said back out loud.
//
// `listening` is the state that most needs saying. When the agent leaves the
// microphone open, the household can answer with no wake word — and someone
// reading the transcript afterwards should be able to see that the room was live,
// because nothing else on screen would reveal it.
function SpokenAloudNote({
  heard,
  listening,
  quote,
}: {
  heard?: boolean
  listening?: boolean
  quote?: string
}) {
  return (
    <div className="trow__voice-note">
      <span aria-hidden>{heard ? '🎙' : '🔊'}</span>{' '}
      {heard ? 'Spoken aloud' : quote ? <>Said aloud: “{quote}”</> : 'Said aloud'}
      {listening ? <span className="trow__voice-listening"> · listening for a reply</span> : null}
    </div>
  )
}

// hearth/1 envelope: server prefixes user-side relayed messages with
//   hearth/1 {json}\n\n<body>
// so multi-user transcripts can attribute messages and (later) carry
// mentions / reply_to metadata. Parse it out so we can render the
// sender's name in the row header and keep the body clean.
//
// Parsers MUST ignore unknown JSON keys for forward compatibility.
type HearthMsgUser = { id?: string; name?: string }
type HearthEnvelope = {
  // hearth/2: `from` is the typed sender (kind device|agent|system — may be a
  // shared device with no person); `human` is the person behind it. On hearth/1
  // `from` IS the human and `human` is absent. Read the human as `human ?? from`.
  from?: HearthMsgUser & { kind?: string }
  human?: HearthMsgUser
  mid?: string
  ts?: string
  mentions?: HearthMsgUser[]
  reply_to?: string
  // Voice fields. Present only when kind="voice" — a message someone SPOKE to a
  // Home Assistant satellite. `said` is the spoken words on their own; the body
  // of a voice message also carries ~700 characters of agent-directed preamble,
  // which is for the agent and not for anyone reading the transcript.
  voice?: { device?: string; said?: string }
  // system_event fields. Present only when kind="system_event".
  kind?: string
  event?: string
  request_id?: string
  tool?: string
  notified?: HearthMsgUser[]
  authorized?: HearthMsgUser[]
  actor?: HearthMsgUser
  outcome?: string
}

type ParsedEnvelope = {
  envelope: HearthEnvelope | null
  body: string
}

// A user transcript entry can concatenate SEVERAL hearth/1 segments back-to-back.
// Claude's runtime coalesces queued stdin submits into ONE `user` entry when they
// pile up while the agent is mid-turn, and the daemon injects each inbound message
// (human relay_input AND system_events alike) as its own submit (see daemon_ws.go).
// So e.g. a resource_granted system_event immediately followed by two human
// messages lands as one entry whose text is four envelopes in a row. The merge is
// lossless — every envelope survives verbatim inline — so we split them back out
// and classify/render each independently. (Parsing only the FIRST envelope, as an
// earlier version did, misclassified the whole blob by its leading segment and hid
// the human messages behind it — the bug this replaces.)
// hearth/1 and hearth/2 share a 9-char prefix and differ only in the `from`
// shape. The reader is version-agnostic: it accepts either prefix and resolves
// the human as `human ?? from` at render time.
const HEARTH_HDR_LEN = 'hearth/1 '.length
function isHearthHeaderAt(text: string, i: number): boolean {
  return text.startsWith('hearth/1 ', i) || text.startsWith('hearth/2 ', i)
}

export function parseHearthSegments(text: string): ParsedEnvelope[] {
  if (!isHearthHeaderAt(text, 0)) return [{ envelope: null, body: text }]
  const segments: ParsedEnvelope[] = []
  let i = 0
  while (i < text.length) {
    const nl = text.indexOf('\n', i)
    if (nl < 0) {
      // No header terminator — not a real envelope; emit the remainder as body.
      segments.push({ envelope: null, body: text.slice(i) })
      break
    }
    let env: HearthEnvelope | null = null
    try {
      env = JSON.parse(text.slice(i + HEARTH_HDR_LEN, nl))
    } catch {
      env = null
    }
    if (env === null) {
      segments.push({ envelope: null, body: text.slice(i) })
      break
    }
    // Body starts after the header line, skipping the optional blank separator.
    let bodyStart = nl + 1
    if (text[bodyStart] === '\n') bodyStart += 1
    // Coalesced segments are concatenated with no separator, so this body runs
    // until the next VALID hearth/1 header (or end of text).
    const next = nextSegmentStart(text, bodyStart)
    const bodyEnd = next < 0 ? text.length : next
    segments.push({ envelope: env, body: text.slice(bodyStart, bodyEnd) })
    if (next < 0) break
    i = next
  }
  return segments.length ? segments : [{ envelope: null, body: text }]
}

// Index of the next `hearth/1 {…}` whose header line parses as JSON, at or after
// `from`. Validating the JSON (not just matching the literal) keeps a body that
// happens to contain the string "hearth/1 {" from being mistaken for a boundary.
function nextSegmentStart(text: string, from: number): number {
  let idx = from
  while (idx < text.length) {
    // Earliest of the two version literals at/after idx.
    const a = text.indexOf('hearth/1 {', idx)
    const b = text.indexOf('hearth/2 {', idx)
    const cand = a < 0 ? b : b < 0 ? a : Math.min(a, b)
    if (cand < 0) return -1
    const nl = text.indexOf('\n', cand)
    if (nl > 0) {
      try {
        JSON.parse(text.slice(cand + HEARTH_HDR_LEN, nl))
        return cand
      } catch {
        // Not a real header here — keep scanning.
      }
    }
    idx = cand + 1
  }
  return -1
}

// SystemEventRow renders a server-emitted hearth/1 envelope with
// kind="system_event" as a centered italic label, matching the iMessage
// "system note" style. Falls back to the body string for unknown events.
function SystemEventRow({ body }: { body: string }) {
  return (
    <div className="trow trow--system">
      <span className="trow__system-label">{body}</span>
    </div>
  )
}

// Renders a scheduled-Routine kickoff — the automated turn the scheduler
// injected — as a distinct banner so it reads as "the routine fired," not as a
// human message.
function RoutineBannerRow({ body }: { body: string }) {
  return (
    <div className="trow trow--routine">
      <div className="trow__routine-badge">⏰ Routine</div>
      <div className="trow__routine-body">
        <Markdown source={body} />
      </div>
    </div>
  )
}

function relTime(ts: number): string {
  const diff = Date.now() - ts
  const mins = Math.round(diff / 60_000)
  if (mins < 1) return 'now'
  if (mins < 60) return `${mins}m`
  const hrs = Math.round(mins / 60)
  if (hrs < 24) return `${hrs}h`
  const days = Math.round(hrs / 24)
  return `${days}d`
}
